import React, { useContext, useEffect, useState } from 'react'
import { Grid } from '../../components/common/grid/Grid'
import { GridItem } from '../../components/common/grid/GridItem'
import { ControlledRTFInput } from '../../components/common/inputs/ControlledRTFInput'
import { ControlledDropDown } from '../../components/common/inputs/Dropdown'
import { ControlledTextInput, TextInput } from '../../components/common/inputs/TextInput'
import { enumValuesToOptions } from '../../helpers/enumHelper'
import { TextOutput } from '../../components/common/outputs/TextOutput'
import { Button, Field, makeStyles, tokens } from '@fluentui/react-components'
import { DemandArea, Demand, DemandStatus, DemandSubType, DemandType, Origin, DemandSearchResultDTO, DemandLevel, Clause } from '../../api/schemas/schema'
import { UseFormReturn } from 'react-hook-form'
import { DataContext } from '../../App'
import { SelectedDemandModal, updateDemand } from '../../helpers/demandHelper'
import { useMsal } from '@azure/msal-react'
import { SubDemands } from '../../components/demand/SubDemands'
import { getDemandLeveLOptions } from '../../helpers/optionsHelper'
import { ParentDemandModal } from '../../components/demand/ParentDemandModal'
import { Modal } from '../../components/common/modal/Modal'
import { DemandSearchBox } from '../../components/demand/DemandSearchBox'
import PersonaOutput from '../../components/common/outputs/PersonaOutput'
import { t } from '../../i18n/i18n'
import { formatDate } from '../../helpers/dateHelper'
import { createClause, deleteClause, getClauseById, updateClause } from '../../helpers/clauseHelper'
import { IDropdownOption } from '../../interfaces/IDropdownOption'
import { ControlledDemandModulesDropdown } from '../Customer/ControlledDemandModulesDropdown'
import { Delete20Regular } from '@fluentui/react-icons'

const useStyles = makeStyles({
   marginRight: {
      marginRight: tokens.spacingHorizontalXL,
   },
   smallGap: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXL,
   },
   flexEnd: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center' },
   flexBtw: { display: 'flex', justifyContent: 'space-between', paddingRight: tokens.spacingHorizontalXXXL },
   flex: { display: 'flex', gap: tokens.spacingHorizontalS },
   resetLink: {
      color: tokens.colorNeutralForeground1,
   },
   semibold: {
      fontWeight: tokens.fontWeightSemibold,
   },
   editor: {
      minHeight: '294px',
   },
   grid: {
      paddingTop: tokens.spacingVerticalXXL,
   },

   clauseContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: tokens.colorNeutralBackground2,
      padding: tokens.spacingHorizontalM,
      borderRadius: tokens.borderRadiusMedium,
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      marginTop: tokens.spacingVerticalS,
   },
   deleteIconAbsolute: {
      position: 'absolute',
      top: tokens.spacingVerticalS,
      right: tokens.spacingHorizontalS,
   },
   clauseHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
   },
   flexS: {
      display: 'flex',
      gap: tokens.spacingHorizontalS,
   },
})

type DemandFormProps = {
   demandForm: UseFormReturn<Demand, any, undefined>
   setOriginDemand: React.Dispatch<React.SetStateAction<Demand>>
   setOpenModal: React.Dispatch<React.SetStateAction<SelectedDemandModal>>
   setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
   setBread: (parentId: string) => Promise<void>
   originDemand: Demand
   openModal: SelectedDemandModal
   isDisabled: boolean
   isSubmitting: boolean
}
export const DemandForm = ({
   demandForm,
   setOriginDemand,
   setOpenModal,
   setIsSubmitting,
   setBread,
   originDemand,
   openModal,
   isDisabled,
   isSubmitting,
}: DemandFormProps) => {
   const dataContext = useContext(DataContext)
   const { state } = dataContext
   const { accounts } = useMsal()
   const { setValue, watch, reset, control } = demandForm
   const { isDirty } = demandForm.formState
   const classes = useStyles()
   const [targetParentDemand, setTargetParentDemand] = useState<DemandSearchResultDTO>(null)
   const [clause, setClause] = useState<Clause>(null)
   const [originalClause, setOriginalClause] = useState<Clause | null>(null)
   const selectedModuleId = watch('demandComplianceModuleId')
   const clauseId = watch('clauseId')
   const [isDeleting, setIsDeleting] = useState(false)
   const definitions = state.demandComplianceModuleDefinitions || []

   const demandModuleOptions: IDropdownOption[] = definitions.map((d) => ({
      key: d.id,
      text: d.name,
   }))

   const onSaveClick = async () => {
      setIsSubmitting(true)
      setValue('modifiedBy', accounts[0].name)

      const formValues = watch()

      // Remove connection to clasue if module deselected
      if (!selectedModuleId) {
         formValues.clauseId = null
         setValue('clauseId', null)
      }

      const updatedDemand = await updateDemand(formValues, dataContext, reset, setIsSubmitting, setOriginDemand, setBread)

      if (updatedDemand) {
         // If deselected module but a clause is selected -> DELETE
         if (!selectedModuleId && clause?.id) {
            if (clause.id) {
               await deleteClause(clause.id, dataContext)
            }
            setClause(null)
            setOriginalClause(null)
            setValue('clauseId', null)
         }
         // If Module is selected and we have clause data -> CREATE or UPDATE
         else if (selectedModuleId && clause) {
            const clauseWithLink = {
               ...clause,
               text: clause.text || '',
               clause: clause.text || '',
               demandId: updatedDemand.id,
               demandModuleId: selectedModuleId,
               createdBy: clause.createdBy || accounts[0].name,
               modifiedBy: accounts[0].name,
            }

            if (!clause.id) {
               const createdClause = await createClause(clauseWithLink, updatedDemand.id, dataContext)
               if (createdClause) {
                  setClause(createdClause)
                  setOriginalClause(createdClause)
                  setValue('clauseId', createdClause.id)
               }
            } else {
               const updatedClause = await updateClause(clauseWithLink, dataContext)
               if (updatedClause) {
                  setClause(updatedClause)
                  setOriginalClause(updatedClause)
               }
            }
         }
      }

      setTargetParentDemand(null)
      setIsSubmitting(false)
   }

   useEffect(() => {
      if (watch('level') !== originDemand.level) {
         setOpenModal(SelectedDemandModal.PARENT)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [watch('level')])
   useEffect(() => {
      if (targetParentDemand) setValue('parentId', targetParentDemand.id, { shouldDirty: true })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [targetParentDemand?.id])

   useEffect(() => {
      const fetchClauses = async () => {
         if (clauseId) {
            const response = await getClauseById(clauseId, dataContext)

            setClause(response)
            setOriginalClause(response)
         } else {
            setClause(null)
            setOriginalClause(null)
         }
      }
      fetchClauses()
      // Do not add suggested data context to dependencies, will clear the module on update demand
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [clauseId])

   const isClauseDirty = JSON.stringify(clause) !== JSON.stringify(originalClause)

   const canSubmit = (isDirty || isClauseDirty) && !isSubmitting && !isDeleting

   const handleDeleteClause = async (clauseId: string) => {
      setIsDeleting(true)
      await deleteClause(clauseId, dataContext)
      await updateDemand(watch(), dataContext, reset, setIsSubmitting, setOriginDemand, setBread)

      setClause(null)
      setOriginalClause(null)
      setIsDeleting(false)
   }

   const onUndoClick = () => {
      reset(originDemand)
      setClause(originalClause)
      setOpenModal(null)
      setTargetParentDemand(null)
   }
   return (
      <>
         <Grid className={classes.grid}>
            <GridItem size="5/12">
               <ControlledRTFInput
                  name={'text'}
                  control={control}
                  label={t('Demand')}
                  isDisabled={isDisabled || isSubmitting}
                  required={true}
                  editorClassName={classes.editor}
                  watchedValue={watch('text')}
               />
            </GridItem>
            <GridItem className={classes.smallGap} size="2/12">
               <ControlledDropDown
                  control={control}
                  name={'status'}
                  options={enumValuesToOptions(DemandStatus)
                     .filter((x) => x.key !== DemandStatus.SavedButNotSure && (watch('status') === DemandStatus.Archived || x.key !== DemandStatus.Archived))
                     .map((option) => ({
                        ...option,
                     }))}
                  label={t('Status')}
                  disabled={isDisabled || isSubmitting}
               />
               <ControlledDropDown
                  control={control}
                  name={'area'}
                  options={enumValuesToOptions(DemandArea)}
                  label={t('DemandArea')}
                  disabled={watch('level') !== 2 || isDisabled || isSubmitting}
               />
               <ControlledDropDown
                  control={control}
                  name={'type'}
                  options={enumValuesToOptions(DemandType).map((x) => (x.key === DemandType.InsuranceDemand ? { ...x, disabled: true } : x))}
                  label={t('DemandType')}
                  disabled={watch('level') !== 2 || isDisabled || isSubmitting}
               />
               <ControlledDropDown
                  control={control}
                  name={'subType'}
                  options={enumValuesToOptions(DemandSubType)}
                  label={t('SubType')}
                  disabled={isDisabled || isSubmitting}
               />
               <ControlledTextInput control={control} name={'version'} label={t('Version')} disabled={isDisabled || isSubmitting} />
            </GridItem>
            <GridItem className={classes.smallGap} size="4/12">
               <ControlledTextInput control={control} name={'source'} label={t('DemandSource')} disabled={isDisabled || isSubmitting} />
               <ControlledDemandModulesDropdown
                  control={control}
                  name="demandComplianceModuleId"
                  label={t('AdditionalModules')}
                  options={demandModuleOptions}
                  disabled={isDisabled || isSubmitting}
               />
               {selectedModuleId && (
                  <div className={classes.clauseContainer}>
                     <Button
                        className={classes.deleteIconAbsolute}
                        icon={<Delete20Regular />}
                        appearance="subtle"
                        onClick={() => handleDeleteClause(clause.id)}
                        disabled={isDisabled || isSubmitting || isDeleting || !clause?.id}
                     />
                     <div className={classes.clauseHeader}>
                        <div className={classes.flexS}>
                           <div>
                              <TextInput
                                 type="number"
                                 label={t('MainNumber')}
                                 value={clause?.mainNumber || ''}
                                 onChange={(e, data) => setClause((prev) => ({ ...prev!, mainNumber: data.value ? Number(data.value) : 0 }))}
                                 disabled={isDisabled || isSubmitting || isDeleting}
                              />
                           </div>
                           <div>
                              <TextInput
                                 type="number"
                                 label={t('SubNumber')}
                                 value={clause?.subNumber || ''}
                                 onChange={(e, data) => setClause((prev) => ({ ...prev!, subNumber: data.value ? Number(data.value) : 0 }))}
                                 disabled={isDisabled || isSubmitting || isDeleting}
                              />
                           </div>
                        </div>
                     </div>

                     <div style={{ display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalS, marginTop: tokens.spacingVerticalS }}>
                        <div style={{ flexGrow: 1 }}>
                           <TextInput
                              label={t('Clause')}
                              value={clause?.text || ''}
                              onChange={(e, data) => setClause((prev) => ({ ...prev!, text: data.value }))}
                              disabled={isDisabled || isSubmitting || isDeleting}
                           />
                        </div>
                     </div>
                  </div>
               )}
               <ControlledTextInput control={control} name={'urlText'} label={t('UrlText')} disabled={isDisabled || isSubmitting} />
               <ControlledTextInput control={control} name={'url'} label={t('WebLink')} disabled={isDisabled || isSubmitting} />
               <ControlledDropDown
                  control={control}
                  name={'origin'}
                  options={enumValuesToOptions(Origin)}
                  label={t('Origin')}
                  disabled={isDisabled || isSubmitting}
               />
            </GridItem>
            <GridItem className={classes.smallGap} size="1/12">
               <ControlledDropDown
                  control={control}
                  name={'level'}
                  label={t('DemandLevel')}
                  disabled={
                     originDemand.level === DemandLevel.One ||
                     (originDemand.level === DemandLevel.Two && originDemand.children.length > 0) ||
                     isDisabled ||
                     isSubmitting
                  }
                  options={getDemandLeveLOptions(originDemand.level)}
               />
               {watch('level') === DemandLevel.Three && (
                  <DemandSearchBox
                     targetDemand={targetParentDemand ? targetParentDemand : originDemand.parent}
                     setTargetDemand={setTargetParentDemand}
                     placeholder={t('Search') + '...'}
                     targetDemandLevel={watch('level') - 1}
                     filterOutDemand={watch()}
                     label={t('ParentDemand')}
                     required
                     hideSearchIcon
                     isDisabled={isDisabled || isSubmitting}
                  />
               )}
               {originDemand.parentId !== watch('parentId') && watch('level') === DemandLevel.Two && (
                  <Field label={t('NewParentDemand')}>{targetParentDemand.designation}</Field>
               )}
               {watch('level') !== DemandLevel.Three && <SubDemands originDemand={originDemand} />}
            </GridItem>

            <GridItem size="5/12" className={classes.flexBtw}>
               <TextOutput value={watch('publicId').toString()} label={t('ID')} />
               <TextOutput label={t('Modified')} value={formatDate(watch('modifiedDate'), true)} />
               <PersonaOutput value={watch('modifiedBy')} label={t('ModifiedBy')} />
            </GridItem>
            <GridItem size="7/12" className={classes.flexEnd}>
               <Button
                  className={classes.marginRight}
                  disabled={(!isDirty && !isClauseDirty) || isSubmitting}
                  onClick={() => setOpenModal(SelectedDemandModal.UNDO)}
               >
                  {t('Undo')}
               </Button>
               <Button appearance="primary" onClick={async () => onSaveClick()} disabled={!canSubmit}>
                  {t('Save')}
               </Button>
            </GridItem>
         </Grid>
         <Modal
            open={openModal === SelectedDemandModal.UNDO}
            title={t('UndoChanges')}
            actions={
               <>
                  <Button onClick={() => setOpenModal(null)}>{t('Cancel')}</Button>
                  <Button onClick={() => onUndoClick()} appearance="primary">
                     {t('Continue')}
                  </Button>
               </>
            }
            children={t('UndoDemandChanges')}
         />
         <ParentDemandModal
            targetDemandLevel={watch('level') - 1}
            demandForm={demandForm}
            setOpenModal={setOpenModal}
            openModal={openModal}
            setTargetDemand={setTargetParentDemand}
            targetDemand={targetParentDemand}
         />
      </>
   )
}
